import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Image as ImageIcon,
  Music,
  Code2,
  MessageSquare,
  Sparkles,
  TrendingUp,
  Pin,
  ArrowRight,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import GlassCard from '../components/ui/GlassCard';
import ProgressBar from '../components/ui/ProgressBar';
import Skeleton from '../components/ui/Skeleton';
import GenerationCard from '../components/history/GenerationCard';
import { StaggerGroup, staggerItem } from '../components/ui/Reveal';
import Tilt from '../components/fx/Tilt';
import CountUp from '../components/fx/CountUp';
import SplitText from '../components/fx/SplitText';

const QUICK_ACTIONS = [
  { to: '/app/image', label: 'Generate image', icon: ImageIcon, grad: 'from-brand-500/20', color: 'text-brand-300' },
  { to: '/app/music', label: 'Compose music', icon: Music, grad: 'from-accent-cyan/20', color: 'text-accent-cyan' },
  { to: '/app/code', label: 'Write code', icon: Code2, grad: 'from-accent-fuchsia/20', color: 'text-accent-fuchsia' },
  { to: '/app/chat', label: 'Chat with AI', icon: MessageSquare, grad: 'from-accent-amber/20', color: 'text-accent-amber' },
];

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['stats'],
    queryFn: async () => (await api.get('/history/stats')).data,
  });

  const usage = data?.usage;
  const usagePct = usage ? (usage.used / usage.limit) * 100 : 0;

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <p className="text-sm text-content-muted">{greeting()},</p>
        <SplitText
          as="h1"
          text={`${user?.name?.split(' ')[0] || 'Creator'} 👋`}
          stagger={0.05}
          className="font-display text-3xl font-semibold tracking-tight"
        />
      </motion.div>

      {/* Quick actions */}
      <StaggerGroup className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {QUICK_ACTIONS.map((a) => (
          <motion.div key={a.to} variants={staggerItem}>
            <Tilt max={10} scale={1.03}>
              <Link to={a.to}>
                <GlassCard hoverable className="group relative h-full overflow-hidden p-5">
                  <div className={`absolute -right-8 -top-8 h-28 w-28 rounded-full bg-gradient-to-br ${a.grad} to-transparent blur-2xl`} />
                  <a.icon className={`relative h-6 w-6 ${a.color}`} />
                  <p className="relative mt-8 text-sm font-medium">{a.label}</p>
                  <ArrowRight className="relative mt-1 h-4 w-4 text-content-subtle transition-transform group-hover:translate-x-1" />
                </GlassCard>
              </Link>
            </Tilt>
          </motion.div>
        ))}
      </StaggerGroup>

      {/* Stats row */}
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <GlassCard spotlight className="p-5 sm:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-content-muted">
              <Sparkles className="h-4 w-4 text-brand-300" /> Usage this period
            </div>
            <span className="rounded-full bg-brand-500/15 px-2.5 py-0.5 text-xs font-medium text-brand-300">
              {usage?.plan || user?.plan} plan
            </span>
          </div>
          {isLoading ? (
            <Skeleton className="h-8 w-32" />
          ) : (
            <>
              <p className="font-display text-2xl font-semibold">
                <CountUp value={usage?.used ?? 0} />
                <span className="text-base font-normal text-content-subtle"> / {usage?.limit ?? 0}</span>
              </p>
              <ProgressBar value={usagePct} className="mt-3" />
              <p className="mt-2 text-xs text-content-subtle">
                {usage ? `${usage.remaining} generations remaining` : ''}
              </p>
            </>
          )}
        </GlassCard>

        <StatTile loading={isLoading} icon={TrendingUp} label="Total creations" value={data?.total} accent="text-brand-300" />
        <StatTile loading={isLoading} icon={Pin} label="Pinned" value={data?.pinned} accent="text-amber-300" />
      </div>

      {/* Modality breakdown */}
      <div className="mt-4 grid grid-cols-3 gap-3">
        <MiniStat loading={isLoading} icon={ImageIcon} label="Images" value={data?.byModality?.IMAGE} color="text-brand-300" />
        <MiniStat loading={isLoading} icon={Music} label="Tracks" value={data?.byModality?.MUSIC} color="text-accent-cyan" />
        <MiniStat loading={isLoading} icon={Code2} label="Snippets" value={data?.byModality?.CODE} color="text-accent-fuchsia" />
      </div>

      {/* Recent */}
      <div className="mt-10 mb-4 flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold">Recent generations</h2>
        <Link to="/app/history" className="flex items-center gap-1 text-sm text-brand-300 hover:text-brand-200">
          View all <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <GlassCard key={i} className="p-4">
              <Skeleton className="aspect-video w-full" />
              <Skeleton className="mt-3 h-4 w-3/4" />
              <Skeleton className="mt-2 h-3 w-1/2" />
            </GlassCard>
          ))}
        </div>
      ) : data?.recent?.length ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.recent.map((g, i) => (
            <GenerationCard key={g.id} generation={g} index={i} />
          ))}
        </div>
      ) : (
        <GlassCard className="flex flex-col items-center justify-center gap-3 p-12 text-center">
          <Sparkles className="h-8 w-8 text-brand-300" />
          <p className="font-medium">Nothing here yet</p>
          <p className="max-w-xs text-sm text-content-muted">
            Your generations will appear here. Start with an image, a track, or some code.
          </p>
          <Link to="/app/image" className="mt-1 text-sm font-medium text-brand-300 hover:text-brand-200">
            Create your first →
          </Link>
        </GlassCard>
      )}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, accent, loading }) {
  return (
    <Tilt max={8}>
      <GlassCard hoverable className="p-5">
        <Icon className={`h-5 w-5 ${accent}`} />
        <p className="mt-6 text-sm text-content-muted">{label}</p>
        {loading ? (
          <Skeleton className="mt-1 h-7 w-12" />
        ) : (
          <p className="font-display text-2xl font-semibold">
            <CountUp value={value ?? 0} />
          </p>
        )}
      </GlassCard>
    </Tilt>
  );
}

function MiniStat({ icon: Icon, label, value, color, loading }) {
  return (
    <GlassCard spotlight className="flex items-center gap-3 p-4">
      <div className="rounded-lg bg-white/5 p-2">
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <div>
        {loading ? (
          <Skeleton className="h-5 w-8" />
        ) : (
          <p className="text-lg font-semibold">
            <CountUp value={value ?? 0} />
          </p>
        )}
        <p className="text-xs text-content-subtle">{label}</p>
      </div>
    </GlassCard>
  );
}
